<template>
  <div class="device-documentation-section">
    <!-- Device List -->
    <q-list bordered separator>
      <q-item
        v-for="device in devices"
        :key="device.id"
        clickable
        @click="selectDevice(device)"
      >
        <q-item-section avatar>
          <q-avatar color="primary" text-color="white" icon="memory" />
        </q-item-section>

        <!-- LEFT SECTION: Device Name + BRICK Class -->
        <q-item-section>
          <q-item-label>{{ device.name }}</q-item-label>
          <q-item-label caption>
            {{ device.brick_class }}
          </q-item-label>
        </q-item-section>

        <!-- MIDDLE SECTION: Part Info (Horizontal Layout) -->
        <q-item-section v-if="device.device_part">
          <q-item-label class="text-primary">
            Manufacturer: {{ device.device_part.manufacturer }}
          </q-item-label>
          <q-item-label caption>
            Part: {{ device.device_part.part_number }}
          </q-item-label>
        </q-item-section>

        <!-- RIGHT SECTION: Completeness Badge -->
        <q-item-section side>
          <div class="row items-center q-gutter-sm">
            <!-- Completeness Indicator -->
            <q-circular-progress
              :value="getDeviceCompleteness(device.id)"
              size="40px"
              :thickness="0.15"
              :color="getDeviceCompletenessColor(device.id)"
              track-color="grey-3"
            >
              <div class="text-caption">{{ getDeviceCompleteness(device.id) }}%</div>
            </q-circular-progress>

            <!-- Documentation Status -->
            <q-chip
              :color="getDeviceCompletenessColor(device.id)"
              text-color="white"
              dense
            >
              {{ getDeviceDocStatus(device.id) }}
            </q-chip>
          </div>
        </q-item-section>
      </q-item>

      <q-item v-if="devices.length === 0">
        <q-item-section>
          <q-item-label class="text-center text-grey-6">
            No devices found for this equipment
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>


    <!-- Device Documentation Dialog -->
    <q-dialog v-model="deviceDialogOpen" persistent>
      <q-card style="min-width: 800px">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">
            {{ selectedDevice?.name || 'Device' }} Documentation
          </div>
          <div class="text-caption">
            {{ selectedDevice?.brick_class }}
          </div>
        </q-card-section>

        <!-- TABS -->
        <q-card-section class="q-pb-none">
          <q-tabs v-model="deviceTab" class="text-primary" align="left">
            <q-tab name="specs" label="Specifications" icon="settings" />
            <q-tab name="registry_files" label="Product Files" icon="folder_shared">
              <q-badge v-if="registryFileCount > 0" color="primary" floating>
                {{ registryFileCount }}
              </q-badge>
            </q-tab>
            <q-tab name="device_files" label="Device Files" icon="attach_file">
              <q-badge v-if="deviceFileCount > 0" color="primary" floating>
                {{ deviceFileCount }}
              </q-badge>
            </q-tab>
          </q-tabs>
        </q-card-section>

        <q-separator />

        <!-- TAB PANELS -->
        <q-card-section>
          <q-tab-panels v-model="deviceTab" animated>

            <!-- TAB 1: Specifications (existing content) -->
            <q-tab-panel name="specs">
              <div class="q-gutter-md">
                <!-- Device Part Registry Autocomplete -->
                <q-select
                  v-model="selectedPart"
                  label="Device Part (from registry)"
                  :options="partOptionsWithAdd"
                  outlined
                  use-input
                  fill-input
                  clearable
                  option-label="display"
                  option-value="id"
                  hint="Search for a device part by part number, manufacturer, or model"
                  @filter="filterParts"
                  @update:model-value="handlePartSelected"
                >
                  <template #no-option>
                    <q-item>
                      <q-item-section class="text-grey">
                        Type to search device parts...
                      </q-item-section>
                    </q-item>
                  </template>

                  <template #option="scope">
                    <q-item
                      v-bind="scope.itemProps"
                      :class="scope.opt.isAddNew ? 'bg-blue-1' : ''"
                    >
                      <q-item-section v-if="scope.opt.isAddNew" avatar>
                        <q-avatar color="primary" text-color="white" icon="add" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label v-if="scope.opt.isAddNew" class="text-primary text-weight-medium">
                          {{ scope.opt.display }}
                        </q-item-label>
                        <template v-else>
                          <q-item-label>{{ scope.opt.part_number }}</q-item-label>
                          <q-item-label caption>
                            {{ scope.opt.manufacturer }} - {{ scope.opt.model }}
                          </q-item-label>
                          <q-item-label caption class="text-grey-6">
                            {{ scope.opt.brick_class }}
                          </q-item-label>
                        </template>
                      </q-item-section>
                    </q-item>
                  </template>
                </q-select>

                <!-- Product Details & Technical Specifications -->
                <div v-if="selectedProduct" class="q-pa-md bg-grey-2 rounded-borders q-mb-md">
                  <div class="row items-center justify-between q-mb-sm">
                    <div class="text-subtitle2">Product Details:</div>
                    <q-btn
                      flat
                      dense
                      color="primary"
                      icon="edit"
                      label="Edit Product"
                      size="sm"
                      @click="showEditProductDialog"
                    >
                      <q-tooltip>Edit this product (affects all devices using this product)</q-tooltip>
                    </q-btn>
                  </div>
                  <!-- Core Product Info -->
                  <div class="q-mb-md">
                    <div class="text-caption"><strong>Product Name:</strong> {{ selectedProduct.name }}</div>
                    <div class="text-caption"><strong>SKU:</strong> {{ selectedProduct.sku }}</div>
                    <div class="text-caption"><strong>Manufacturer:</strong> {{ selectedProduct.manufacturer || selectedPart?.manufacturer || 'Not specified' }}</div>
                    <div class="text-caption"><strong>Model:</strong> {{ selectedProduct.model || selectedPart?.model || 'Not specified' }}</div>
                    <div v-if="selectedProduct.category" class="text-caption"><strong>Category:</strong> {{ selectedProduct.category }}</div>
                    <div v-if="selectedProduct.description" class="text-caption"><strong>Description:</strong> {{ selectedProduct.description }}</div>
                    <div v-if="selectedProduct.price" class="text-caption"><strong>Price:</strong> ${{ selectedProduct.price }} / {{ selectedProduct.unit || 'each' }}</div>
                    <div v-if="selectedPart?.brick_class" class="text-caption"><strong>BRICK Class:</strong> {{ selectedPart.brick_class }}</div>
                  </div>

                  <!-- Technical Specifications (from Product) -->
                  <div class="text-subtitle2 q-mt-md q-mb-xs">Technical Specifications:</div>
                  <template v-if="productSpecifications && Object.keys(productSpecifications).length > 0">
                    <div v-for="(value, key) in productSpecifications" :key="key" class="text-caption">
                      <strong>{{ formatSpecLabel(key) }}:</strong> {{ value }}
                    </div>
                  </template>
                  <div v-else class="text-caption text-grey-6 text-italic">
                    No technical specifications defined. Click "Edit Product" to add specifications.
                  </div>
                </div>

                <!-- Device Instance Details (site-specific information) -->
                <q-expansion-item
                  v-if="selectedProduct"
                  label="Device Instance Details"
                  icon="assignment"
                  caption="Site-specific information for this installed device"
                  class="q-mb-md"
                >
                  <q-card flat>
                    <q-card-section>
                      <div class="text-caption q-mb-md text-grey-7">
                        Optional: Enter details specific to this particular device installation.
                      </div>
                      <div class="q-gutter-sm">
                        <q-input
                          v-model="instanceDetails.serial_number"
                          label="Serial Number"
                          outlined
                          dense
                          hint="Device serial number"
                        />
                        <q-input
                          v-model="instanceDetails.install_date"
                          label="Installation Date"
                          type="date"
                          outlined
                          dense
                        />
                        <q-input
                          v-model="instanceDetails.location_notes"
                          label="Location Notes"
                          type="textarea"
                          rows="2"
                          outlined
                          dense
                          hint="e.g., 'Above door in corridor', 'Behind access panel'"
                        />
                      </div>
                    </q-card-section>
                  </q-card>
                </q-expansion-item>

                <!-- Fallback: Part Details (when no Product exists yet) -->
                <div v-if="selectedPart && !selectedProduct" class="q-pa-md bg-grey-2 rounded-borders">
                  <div class="row items-center justify-between q-mb-sm">
                    <div class="text-subtitle2">Part Details:</div>
                    <q-btn
                      flat
                      dense
                      color="primary"
                      icon="edit"
                      label="Edit Part"
                      size="sm"
                      @click="showEditPartDialog"
                    >
                      <q-tooltip>Edit this part in the registry</q-tooltip>
                    </q-btn>
                  </div>
                  <div class="text-caption"><strong>Part Number:</strong> {{ selectedPart.part_number }}</div>
                  <div class="text-caption"><strong>Manufacturer:</strong> {{ selectedPart.manufacturer || 'Not specified' }}</div>
                  <div class="text-caption"><strong>Model:</strong> {{ selectedPart.model || 'Not specified' }}</div>
                  <div class="text-caption text-warning q-mt-sm">
                    <q-icon name="warning" size="xs" class="q-mr-xs" />
                    No Product linked to this part. Create a Product to enable full specifications.
                  </div>
                </div>
              </div>
            </q-tab-panel>

            <!-- TAB 2: Product Files -->
            <q-tab-panel name="registry_files">
              <div class="text-subtitle1 q-mb-md">
                <q-icon name="folder_shared" class="q-mr-sm" />
                Product Documentation
              </div>

              <q-card v-if="!selectedProduct" flat bordered class="q-pa-md text-center text-grey-6">
                <q-icon name="info" size="md" class="q-mb-sm" />
                <div>Select a device part in the Specifications tab to view product documentation</div>
              </q-card>

              <div v-else>
                <div class="text-caption text-grey-7 q-mb-md">
                  These files are shared across all devices using this product:
                  <strong>{{ selectedProduct.name || selectedProduct.sku }}</strong>
                  ({{ selectedProduct.manufacturer }} {{ selectedProduct.model }})
                </div>

                <document-file-manager
                  documentable-type="App\Models\Product"
                  :documentable-id="selectedProduct.id"
                  file-type="registry_shared"
                  :affected-devices-count="selectedProduct.site_equipment_devices_count || 0"
                  @file-uploaded="handleRegistryFileUploaded"
                  @file-deleted="handleRegistryFileDeleted"
                />
              </div>
            </q-tab-panel>

            <!-- TAB 3: Device Files (instance_unique) -->
            <q-tab-panel name="device_files">
              <div class="text-subtitle1 q-mb-md">
                <q-icon name="attach_file" class="q-mr-sm" />
                Files for This Device Instance
              </div>
              <div class="text-caption text-grey-7 q-mb-md">
                These files are specific to this device instance only (commissioning reports, as-installed photos, etc.)
              </div>

              <document-file-manager
                documentable-type="App\Models\SiteEquipmentDevice"
                :documentable-id="selectedDevice.id"
                file-type="instance_unique"
                @file-uploaded="handleDeviceFileUploaded"
                @file-deleted="handleDeviceFileDeleted"
              />
            </q-tab-panel>

          </q-tab-panels>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="closeDeviceDialog" />
          <q-btn
            color="primary"
            label="Save"
            @click="saveDeviceDoc"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Add Device Part Dialog (Shared Component) -->
    <AddDevicePartDialog
      v-model="addPartDialogOpen"
      :part-number="addPartInitialData.partNumber"
      :brick-class="addPartInitialData.brickClass"
      @created="handlePartCreated"
    />

    <!-- Edit Device Part Dialog (Shared Component) -->
    <EditDevicePartDialog
      v-model="editPartDialogOpen"
      :part-id="editPartId"
      @updated="handlePartUpdated"
    />
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { api } from '@/services/api';
  import { useQuasar } from 'quasar';
  import DocumentFileManager from './DocumentFileManager.vue';
  import AddDevicePartDialog from './dialogs/AddDevicePartDialog.vue';
  import EditDevicePartDialog from './dialogs/EditDevicePartDialog.vue';

  const $q = useQuasar();

  const props = defineProps({
    equipmentId: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['update:modelValue']);

  const devices = ref([]);
  const deviceDialogOpen = ref(false);
  const selectedDevice = ref(null);
  const selectedPart = ref(null);
  const selectedProduct = ref(null); // Product corresponding to selectedPart
  const partOptions = ref([]);
  const deviceSpecs = ref({});
  const instanceDetails = ref({
    serial_number: '',
    install_date: '',
    location_notes: '',
  });

  // Tab management
  const deviceTab = ref('specs');
  const registryFileCount = ref(0);
  const deviceFileCount = ref(0);

  // Add Device Part Dialog State
  const addPartDialogOpen = ref(false);
  const currentSearchTerm = ref('');
  const addPartInitialData = ref({
    partNumber: '',
    brickClass: '',
  });

  // Specification fields for device specs and edit dialog
  const loadingHierarchy = ref(false);
  const brickClassOptions = ref([]);
  const specificationFields = ref([]);
  const loadingSpecFields = ref(false);

  // Edit Device Part Dialog State (Shared Component)
  const editPartDialogOpen = ref(false);
  const editPartId = ref(null);

  const localValue = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  // Add "Create new part" option to autocomplete when no results found
  const partOptionsWithAdd = computed(() => {
    const options = [...partOptions.value];

    // If user has typed something and there are no results, show "Add new" option
    if (currentSearchTerm.value && currentSearchTerm.value.length >= 2 && options.length === 0) {
      options.push({
        id: '__add_new__',
        isAddNew: true,
        display: `➕ Add "${currentSearchTerm.value}" as new part`,
        searchTerm: currentSearchTerm.value,
      });
    }

    return options;
  });

  // Safely parse specifications - handles string, object, array, and empty cases
  const parsedSpecifications = computed(() => {
    if (!selectedPart.value?.specifications) {
      return null;
    }

    let specs = selectedPart.value.specifications;

    // If it's a string, try to parse it as JSON
    if (typeof specs === 'string') {
      try {
        specs = JSON.parse(specs);
      } catch (e) {
        // Invalid JSON string - return null
        return null;
      }
    }

    // If it's an array, it might be empty
    if (Array.isArray(specs)) {
      return specs.length > 0 ? specs : null;
    }

    // If it's an object, check if it has any properties
    if (typeof specs === 'object' && specs !== null) {
      return Object.keys(specs).length > 0 ? specs : null;
    }

    return null;
  });

  // Safely parse Product specifications - handles string, object, array, and empty cases
  const productSpecifications = computed(() => {
    if (!selectedProduct.value?.specifications) {
      return null;
    }

    let specs = selectedProduct.value.specifications;

    // If it's a string, try to parse it as JSON
    if (typeof specs === 'string') {
      try {
        specs = JSON.parse(specs);
      } catch (e) {
        // Invalid JSON string - return null
        return null;
      }
    }

    // If it's an array, it might be empty
    if (Array.isArray(specs)) {
      return specs.length > 0 ? specs : null;
    }

    // If it's an object, check if it has any properties
    if (typeof specs === 'object' && specs !== null) {
      return Object.keys(specs).length > 0 ? specs : null;
    }

    return null;
  });

  const loadDevices = async () => {
    try {
      const response = await api.get(`/site-equipment/${props.equipmentId}/devices`);
      // Response is auto-unwrapped by api.js, so it's already the devices array
      devices.value = response || [];

      // BUGFIX: Merge device_part data from localValue (saved documentation)
      // This ensures part numbers show even if backend hasn't synced to site_equipment_devices table
      if (localValue.value && localValue.value.length > 0) {
        devices.value = devices.value.map((device) => {
          const savedDoc = localValue.value.find((d) => d.device_id === device.id);
          if (savedDoc && savedDoc.device_part_id && !device.device_part) {
            // Device has a part in documentation but not in the devices table
            // Load the part details to display
            loadDevicePartForDevice(device.id, savedDoc.device_part_id);
          }
          return device;
        });
      }
    } catch (error) {
      console.error('Failed to load saved documentation:', error);
    }
  };

  // Helper to load device part details for a specific device
  const loadDevicePartForDevice = async (deviceId, partId) => {
    try {
      const response = await api.get(`/device-parts/${partId}`);
      const part = response || {};

      // Update the device in the devices array to include the part
      devices.value = devices.value.map((device) => {
        if (device.id === deviceId) {
          return {
            ...device,
            device_part_id: partId,
            device_part: {
              id: part.id,
              part_number: part.part_number,
              manufacturer: part.manufacturer,
              model: part.model,
            },
          };
        }
        return device;
      });
    } catch (error) {
      console.error('Failed to load device part details:', error);
    }
  };

  const selectDevice = async (device) => {
    selectedDevice.value = device;
    deviceDialogOpen.value = true;
    deviceTab.value = 'specs'; // Reset to specs tab

    // Load specification fields for this device's BRICK class
    if (device.brick_class) {
      await loadSpecificationFields(device.brick_class);
    }

    // Load existing documentation for this device
    const existingDoc = localValue.value.find((d) => d.device_id === device.id);

    // Determine which device_part_id to use (documentation takes priority, then device itself)
    const devicePartId = existingDoc?.device_part_id || device.device_part_id;

    if (existingDoc) {
      deviceSpecs.value = { ...existingDoc.specifications };
      // Load instance details if they exist
      instanceDetails.value = existingDoc.instance_details || {
        serial_number: '',
        install_date: '',
        location_notes: '',
      };
    } else {
      // Initialize with empty values for all fields
      const specs = {};
      specificationFields.value.forEach((field) => {
        specs[field.key] = '';
      });
      deviceSpecs.value = specs;
      // Reset instance details
      instanceDetails.value = {
        serial_number: '',
        install_date: '',
        location_notes: '',
      };
    }

    // Load part if device_part_id exists (from documentation OR from device itself)
    if (devicePartId) {
      await loadDevicePart(devicePartId);
      // loadDevicePart now also loads selectedProduct, so use that for file count
      if (selectedProduct.value?.id) {
        await loadRegistryFileCount(selectedProduct.value.id);
      }
    }

    // Load device instance file count
    await loadDeviceFileCount(device.id);
  };

  const closeDeviceDialog = () => {
    deviceDialogOpen.value = false;
    selectedDevice.value = null;
    selectedPart.value = null;
    selectedProduct.value = null;
    deviceSpecs.value = {};
    deviceTab.value = 'specs';
    registryFileCount.value = 0;
    deviceFileCount.value = 0;
  };

  const filterParts = async (val, update) => {
    currentSearchTerm.value = val;

    if (val.length < 2) {
      update(() => {
        partOptions.value = [];
      });
      return;
    }

    try {
      const response = await api.get('/device-parts/search', {
        q: val,
        brick_class: selectedDevice.value?.brick_class,
      });

      // Handle both wrapped and unwrapped responses
      const parts = response.data || response || [];

      update(() => {
        partOptions.value = parts.map((part) => ({
          ...part,
          display: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        }));
      });
    } catch (error) {
      update(() => {
        partOptions.value = [];
      });
    }
  };

  const loadDevicePart = async (partId) => {
    try {
      const response = await api.get(`/device-parts/${partId}`);
      // Handle both wrapped and unwrapped responses
      const part = response.data || response;
      selectedPart.value = {
        ...part,
        display: `${part.part_number} - ${part.manufacturer} ${part.model}`,
      };

      // CRITICAL FIX: Add the loaded part to partOptions so dropdown shows it
      // Without this, q-select with use-input shows empty because partOptions is []
      partOptions.value = [selectedPart.value];

      // Load the corresponding Product for this DevicePartRegistry entry
      await loadProductForDevicePart(partId);
    } catch (error) {
      console.error('Failed to load device part:', error);
    }
  };

  // Load the Product that corresponds to a DevicePartRegistry entry
  const loadProductForDevicePart = async (devicePartId) => {
    if (!devicePartId) {
      selectedProduct.value = null;
      return;
    }

    try {
      const response = await api.get('/products', {
        device_part_id: devicePartId,
        no_pagination: true,
      });

      // Response is auto-unwrapped, get the data array
      const products = response.data || response || [];
      if (products.length > 0) {
        selectedProduct.value = products[0];
      } else {
        selectedProduct.value = null;
      }
    } catch (error) {
      selectedProduct.value = null;
    }
  };

  const handlePartSelected = async (part) => {
    if (!part) {
      selectedProduct.value = null;
      return;
    }

    // Check if user selected "Add new part" option
    if (part.isAddNew) {
      // Open dialog with pre-filled part number and brick_class
      showAddPartDialog(part.searchTerm);
      // Clear selection to show dialog properly
      selectedPart.value = null;
      selectedProduct.value = null;
      return;
    }

    // Normal part selection - auto-fill specs from registry
    deviceSpecs.value = { ...(part.specifications || {}) };

    // Load the corresponding Product for this part
    await loadProductForDevicePart(part.id);
  };

  const saveDeviceDoc = () => {
    const deviceDoc = {
      device_id: selectedDevice.value.id,
      device_part_id: selectedPart.value?.id || null,
      specifications: deviceSpecs.value,
      instance_details: instanceDetails.value,
    };

    // Update or add device documentation
    const existingIndex = localValue.value.findIndex((d) => d.device_id === selectedDevice.value.id);
    if (existingIndex >= 0) {
      localValue.value[existingIndex] = deviceDoc;
    } else {
      localValue.value.push(deviceDoc);
    }

    // BUGFIX: Update the devices array to show the part number in the list
    // Find the device in the devices array and update its device_part relationship
    const deviceIndex = devices.value.findIndex((d) => d.id === selectedDevice.value.id);
    if (deviceIndex >= 0) {
      devices.value = devices.value.map((device) => {
        if (device.id === selectedDevice.value.id) {
          return {
            ...device,
            device_part_id: selectedPart.value?.id || null,
            device_part: selectedPart.value ? {
              id: selectedPart.value.id,
              part_number: selectedPart.value.part_number,
              manufacturer: selectedPart.value.manufacturer,
              model: selectedPart.value.model,
            } : null,
          };
        }
        return device;
      });
    }

    $q.notify({
      type: 'positive',
      message: 'Device documentation updated',
    });

    closeDeviceDialog();
  };

  const getDeviceCompleteness = (deviceId) => {
    // Get device data from devices array
    const device = devices.value.find((d) => d.id === deviceId);
    // Get documentation from localValue
    const doc = localValue.value.find((d) => d.device_id === deviceId);

    let score = 0;

    // Name: 20%
    if (device?.name && device.name.trim() !== '') {
      score += 20;
    }

    // BRICK class: 20%
    if (device?.brick_class && device.brick_class.trim() !== '') {
      score += 20;
    }

    // Device part OR manufacturer/model: 30%
    const hasPart = doc?.device_part_id || device?.device_part_id;
    const hasManufacturer = device?.device_part?.manufacturer;
    const hasModel = device?.device_part?.model;

    if (hasPart) {
      score += 30;
    } else if (hasManufacturer && hasModel) {
      score += 30;
    } else if (hasManufacturer || hasModel) {
      score += 15; // Partial credit
    }

    // Specifications: 30%
    const hasSpecs = doc?.specifications && Object.keys(doc.specifications).length > 0;
    if (hasSpecs) {
      score += 30;
    }

    return score;
  };

  const getDeviceCompletenessColor = (deviceId) => {
    const completeness = getDeviceCompleteness(deviceId);
    if (completeness >= 90) {return 'green';}
    if (completeness >= 70) {return 'light-green';}
    if (completeness >= 50) {return 'orange';}
    if (completeness >= 30) {return 'deep-orange';}
    return 'red';
  };

  const getDeviceDocStatus = (deviceId) => {
    const completeness = getDeviceCompleteness(deviceId);
    if (completeness >= 90) {return 'Documented';}
    if (completeness >= 60) {return 'Well Documented';}
    if (completeness >= 40) {return 'Partial';}
    if (completeness >= 20) {return 'Minimal';}
    return 'Undocumented';
  };

  // File event handlers
  const handleRegistryFileUploaded = () => {
    registryFileCount.value++;
  };

  const handleRegistryFileDeleted = () => {
    registryFileCount.value = Math.max(0, registryFileCount.value - 1);
  };

  const handleDeviceFileUploaded = () => {
    deviceFileCount.value++;
  };

  const handleDeviceFileDeleted = () => {
    deviceFileCount.value = Math.max(0, deviceFileCount.value - 1);
  };

  // File count loading - now uses Product instead of DevicePartRegistry
  const loadRegistryFileCount = async (productId) => {
    if (!productId) {
      registryFileCount.value = 0;
      return;
    }

    try {
      const response = await api.get('/documentation-files', {
        documentable_type: 'App\\Models\\Product',
        documentable_id: productId,
        file_type: 'registry_shared',
      });

      registryFileCount.value = response.data?.length || 0;
    } catch (error) {
      registryFileCount.value = 0;
    }
  };

  const loadDeviceFileCount = async (deviceId) => {
    if (!deviceId) {
      deviceFileCount.value = 0;
      return;
    }

    try {
      const response = await api.get('/documentation-files', {
        documentable_type: 'App\\Models\\SiteEquipmentDevice',
        documentable_id: deviceId,
        file_type: 'instance_unique',
      });

      deviceFileCount.value = response.data?.length || 0;
    } catch (error) {
      deviceFileCount.value = 0;
    }
  };

  const showAddPartDialog = (partNumber = '') => {
    addPartInitialData.value = {
      partNumber: partNumber,
      brickClass: selectedDevice.value?.brick_class || '',
    };
    addPartDialogOpen.value = true;
  };

  const loadBrickHierarchy = async (brickClass) => {
    loadingHierarchy.value = true;
    try {
      const response = await api.get('/brick-schema/hierarchy', {
        brick_class: brickClass,
      });

      const hierarchy = response.data || response || [];

      // Transform hierarchy into dropdown options with descriptions
      brickClassOptions.value = hierarchy.map((cls, index) => {
        let description = '';
        if (index === 0) {
          description = 'Most specific - exact match for this device';
        } else if (index === hierarchy.length - 1) {
          description = 'Most general - works for any related device';
        } else {
          description = `Parent class (level ${index})`;
        }

        return {
          label: cls,
          value: cls,
          description: description,
        };
      });
    } catch (error) {
      // Fallback to just the current class
      brickClassOptions.value = [{
        label: brickClass,
        value: brickClass,
        description: 'Current device class',
      }];
    } finally {
      loadingHierarchy.value = false;
    }
  };

  const loadSpecificationFields = async (brickClass) => {
    if (!brickClass) {
      specificationFields.value = [];
      return;
    }

    loadingSpecFields.value = true;
    try {
      const response = await api.get('/brick-schema/specification-fields', {
        brick_class: brickClass,
      });

      specificationFields.value = response.data || response || [];
    } catch (error) {
      // Fallback to generic fields
      specificationFields.value = [
        { key: 'description', label: 'Description', type: 'textarea', hint: 'Technical specifications' },
      ];
    } finally {
      loadingSpecFields.value = false;
    }
  };

  const handlePartCreated = async (createdPart) => {
    // Auto-select the newly created part if a device is currently selected
    if (selectedDevice.value) {
      selectedPart.value = {
        ...createdPart,
        display: `${createdPart.part_number} - ${createdPart.manufacturer} ${createdPart.model}`,
      };
      deviceSpecs.value = { ...(createdPart.specifications || {}) };

      // CRITICAL FIX: Add the created part to partOptions so dropdown shows it
      partOptions.value = [selectedPart.value];

      // Load the corresponding Product for this part
      await loadProductForDevicePart(createdPart.id);
    }
  };

  const showEditPartDialog = () => {
    if (!selectedPart.value) {return;}

    editPartId.value = selectedPart.value.id;
    editPartDialogOpen.value = true;
  };

  // Format spec keys like "range_min" to "Range Min"
  const formatSpecLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const showEditProductDialog = () => {
    if (!selectedProduct.value?.id) {return;}

    // Open the Products page with this product selected for editing
    // Using a new tab so user doesn't lose their place
    window.open(`/products?edit=${selectedProduct.value.id}`, '_blank');
  };

  const handlePartUpdated = (updatedPart) => {
    // Update the selected part with new data
    if (selectedDevice.value && selectedPart.value?.id === updatedPart.id) {
      selectedPart.value = {
        ...updatedPart,
        display: `${updatedPart.part_number} - ${updatedPart.manufacturer} ${updatedPart.model}`,
      };
      // Update device specs to reflect registry changes
      deviceSpecs.value = { ...(updatedPart.specifications || {}) };
    }
  };

  onMounted(() => {
    loadDevices();
  });

  watch(() => props.equipmentId, () => {
    loadDevices();
  });

  // Watch for product selection changes to update registry file count
  watch(() => selectedProduct.value?.id, async (newProductId) => {
    if (newProductId) {
      await loadRegistryFileCount(newProductId);
    } else {
      registryFileCount.value = 0;
    }
  });
</script>

<style scoped>
.device-documentation-section {
  width: 100%;
}
</style>
